import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { env } from "@/lib/env";
import type { ChatContext } from "@/lib/buildChatContext";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export function useChat(context: ChatContext) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setLoading(true);

      try {
        // Build history for the API (exclude the message we just added)
        const history = messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        // Get the session token for the Authorization header
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token ?? "";
        const anonKey = env.supabase.publishableKey;
        const supabaseUrl = env.supabase.url;
        const edgeFunctionUrl = `${supabaseUrl}/functions/v1/chat-assistant`;

        const response = await fetch(edgeFunctionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: anonKey,
          },
          body: JSON.stringify({
            message: text.trim(),
            context,
            history,
          }),
        });

        if (!response.ok) {
          throw new Error(`Edge function returned ${response.status}`);
        }

        const contentType = response.headers.get("Content-Type") ?? "";

        if (contentType.includes("text/event-stream") && response.body) {
          // Streaming path: read SSE tokens and append them progressively
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let assistantText = "";
          const assistantId = crypto.randomUUID();

          // Add empty assistant message placeholder
          setMessages((prev) => [...prev, { id: assistantId, role: "assistant", content: "" }]);

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split("\n")) {
              if (line.startsWith("data: ") && line !== "data: [DONE]") {
                try {
                  const parsed = JSON.parse(line.slice(6));
                  if (parsed.token) {
                    assistantText += parsed.token;
                    setMessages((prev) =>
                      prev.map((m) => (m.id === assistantId ? { ...m, content: assistantText } : m))
                    );
                  }
                } catch {
                  // Skip malformed lines
                }
              }
            }
          }
        } else {
          // Fallback: non-streaming JSON response
          const data = await response.json();
          const assistantContent =
            typeof data === "object" && data !== null && "message" in data
              ? String(data.message)
              : String(data);

          const assistantMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: assistantContent,
          };
          setMessages((prev) => [...prev, assistantMessage]);
        }
      } catch (_err) {
        const errorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, context]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, loading, sendMessage, clearMessages };
}
